import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";

const NAV_ITEMS = [
  { label: "What We Buy", href: "#what-we-buy" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "FAQ", href: "#faq" },
];

const FAMILY_SITES = [
  {
    to: "/nj",
    label: "Heritage Homecare",
    region: "Hoboken, NJ",
  },
  {
    to: "/ny",
    label: "Heritage Companion Care",
    region: "New York, NY",
  },
];

export const CONTACT_EMAIL = "info@heracq.com";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [familyOpen, setFamilyOpen] = useState(false);
  const familyRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (familyRef.current && !familyRef.current.contains(e.target)) {
        setFamilyOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const scrollToForm = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    document.getElementById("submit")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Family strip — introduces sister sites, scrolls away with page */}
      <div
        data-testid="family-strip"
        className="w-full bg-[#1A2621] text-[#FDFBF7]/90"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-9 flex items-center justify-between gap-4 text-[11px] md:text-xs">
          <span className="uppercase tracking-[0.26em] text-[#FDFBF7]/60 hidden sm:inline">
            A Heritage Homecare Holdings Group company
          </span>
          <div className="flex items-center gap-5 md:gap-7 ml-auto">
            <Link
              to="/nj"
              data-testid="family-strip-nj"
              className="inline-flex items-center gap-1.5 hover:text-[#C86A51] transition-colors"
            >
              Heritage Homecare
              <span className="text-[#FDFBF7]/40">·</span>
              <span className="uppercase tracking-[0.22em] text-[#FDFBF7]/70">
                NJ
              </span>
              <ArrowUpRight size={12} className="opacity-70" />
            </Link>
            <Link
              to="/ny"
              data-testid="family-strip-ny"
              className="inline-flex items-center gap-1.5 hover:text-[#C86A51] transition-colors"
            >
              Heritage Companion Care
              <span className="text-[#FDFBF7]/40">·</span>
              <span className="uppercase tracking-[0.22em] text-[#FDFBF7]/70">
                NY
              </span>
              <ArrowUpRight size={12} className="opacity-70" />
            </Link>
          </div>
        </div>
      </div>

      <header
        data-testid="site-navbar"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#D5DACF]"
            : "bg-[#FDFBF7]"
        }`}
      >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="w-9 h-9 rounded-full bg-[#284A3B] flex items-center justify-center text-[#FDFBF7] font-serif-display text-lg leading-none">
            H
          </span>
          <span className="font-serif-display text-xl md:text-2xl text-[#1A2621] leading-tight">
            Heritage
            <span className="text-[#4A5D54] text-base ml-1.5 hidden sm:inline">
              Homecare Holdings Group
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.href.slice(1)}`}
              className="text-sm text-[#1A2621] hover:text-[#C86A51] transition-colors"
            >
              {item.label}
            </a>
          ))}

          <div ref={familyRef} className="relative">
            <button
              type="button"
              data-testid="nav-family-trigger"
              onClick={() => setFamilyOpen((s) => !s)}
              className="inline-flex items-center gap-1 text-sm text-[#1A2621] hover:text-[#C86A51] transition-colors"
            >
              Our Care Services
              <ChevronDown
                size={14}
                className={`transition-transform ${familyOpen ? "rotate-180" : ""}`}
              />
            </button>

            {familyOpen && (
              <div
                data-testid="nav-family-menu"
                className="absolute right-0 mt-3 w-72 rounded-2xl border border-[#D5DACF] bg-[#FDFBF7]/95 backdrop-blur-xl shadow-lg p-2"
              >
                {FAMILY_SITES.map((f) => (
                  <Link
                    key={f.to}
                    to={f.to}
                    onClick={() => setFamilyOpen(false)}
                    data-testid={`nav-family-${f.to.slice(1)}`}
                    className="flex items-start justify-between gap-3 rounded-xl px-4 py-3 hover:bg-[#E6EAE3] transition-colors group"
                  >
                    <span>
                      <span className="block font-serif-display text-base text-[#1A2621] leading-tight">
                        {f.label}
                      </span>
                      <span className="block text-xs uppercase tracking-[0.2em] text-[#4A5D54] mt-1">
                        {f.region}
                      </span>
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="text-[#4A5D54] group-hover:text-[#C86A51] mt-1 shrink-0"
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href="#submit"
            onClick={scrollToForm}
            data-testid="navbar-cta"
            className="bg-[#C86A51] hover:bg-[#B35A42] text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors shadow-sm"
          >
            Submit Your Business
          </a>
        </nav>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 rounded-full border border-[#D5DACF] bg-white/70"
          onClick={() => setMobileOpen((s) => !s)}
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FDFBF7] border-t border-[#D5DACF] px-6 py-6 flex flex-col gap-5"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`mobile-nav-${item.href.slice(1)}`}
              className="text-base text-[#1A2621]"
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </a>
          ))}

          <div className="pt-3 border-t border-[#D5DACF]">
            <div className="text-xs uppercase tracking-[0.22em] text-[#4A5D54] mb-3">
              Our Care Services
            </div>
            <div className="flex flex-col gap-3">
              {FAMILY_SITES.map((f) => (
                <Link
                  key={f.to}
                  to={f.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-family-${f.to.slice(1)}`}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="font-serif-display text-lg text-[#1A2621] leading-tight">
                    {f.label}
                    <span className="block text-xs uppercase tracking-[0.2em] text-[#4A5D54] mt-1">
                      {f.region}
                    </span>
                  </span>
                  <ArrowUpRight size={16} className="text-[#4A5D54]" />
                </Link>
              ))}
            </div>
          </div>

          <a
            href="#submit"
            onClick={scrollToForm}
            data-testid="mobile-navbar-cta"
            className="bg-[#C86A51] text-white rounded-full px-6 py-3 text-sm font-medium text-center"
          >
            Submit Your Business
          </a>
        </div>
      )}
    </header>
    </>
  );
}
